import { useRef, useState } from "react";

import type {
  AIHomeworkResponse,
  AIHomeworkService,
  AIHomeworkVote,
  AIHomeworkVoteState,
  HintResponse,
  PracticeQuestionResponse,
  QuestionAIState,
} from "./aiHomeworkTypes";
import type { NormalizedQuestionData } from "@/features/questions/questionNormalizer";

interface UseQuestionAIParams {
  aiHomeworkService: AIHomeworkService | null;
  chapterTitle: string;
  componentId: string;
  course: {
    parentSubject?: string;
  };
  // Hints and practice problems are independent settings; each request is gated
  // on its own flag so turning one off never disables the other.
  isHintEnabled: boolean;
  isPracticeEnabled: boolean;
  question: NormalizedQuestionData;
}

const initialVoteState: AIHomeworkVoteState = {
  error: null,
  isVoting: false,
  userVote: null,
};

function getErrorMessage(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback;
}

function toPracticeResponse(
  response: AIHomeworkResponse,
  previous: PracticeQuestionResponse,
): PracticeQuestionResponse {
  // A regenerated response replaces the content but arrives through the vote
  // endpoint, which returns the chat response shape rather than a practice
  // problem. Keep the structured question already on screen and take the new
  // text, rather than re-parsing JSON out of the response as the old prose
  // contract required.
  return {
    ...response,
    question: {
      ...previous.question,
      text: response.currentResponse || previous.question.text,
    },
  };
}

export function useQuestionAI({
  aiHomeworkService,
  chapterTitle,
  componentId,
  course,
  isHintEnabled,
  isPracticeEnabled,
  question,
}: UseQuestionAIParams): QuestionAIState {
  const [hintResponse, setHintResponse] = useState<HintResponse | null>(null);
  const [hintError, setHintError] = useState<string | null>(null);
  const [isHintLoading, setIsHintLoading] = useState(false);
  const [isHintRegenerating, setIsHintRegenerating] = useState(false);
  const [hintStatusMessage, setHintStatusMessage] = useState<string | null>(
    null,
  );
  const [hintVote, setHintVote] =
    useState<AIHomeworkVoteState>(initialVoteState);
  const hintVoteRef = useRef<AIHomeworkVote | null>(null);

  const [practiceResponse, setPracticeResponse] =
    useState<PracticeQuestionResponse | null>(null);
  const [practiceError, setPracticeError] = useState<string | null>(null);
  const [isPracticeLoading, setIsPracticeLoading] = useState(false);
  const [isPracticeRegenerating, setIsPracticeRegenerating] = useState(false);
  const [practiceStatusMessage, setPracticeStatusMessage] = useState<
    string | null
  >(null);
  const [practiceCount, setPracticeCount] = useState(0);
  const [selectedPracticeAnswer, setSelectedPracticeAnswer] = useState<
    number | null
  >(null);
  const [isPracticeAnswerRevealed, setIsPracticeAnswerRevealed] =
    useState(false);
  const [practiceVote, setPracticeVote] =
    useState<AIHomeworkVoteState>(initialVoteState);
  const practiceVoteRef = useRef<AIHomeworkVote | null>(null);

  async function requestHint() {
    if (!isHintEnabled || !aiHomeworkService) {
      setHintError("AI hints are not available for this question.");
      return;
    }

    setHintError(null);
    setHintStatusMessage(null);
    setIsHintRegenerating(false);
    setIsHintLoading(true);

    try {
      const response = await aiHomeworkService.getHint({
        componentId,
        course,
        questionUid: question.uid,
      });

      setHintResponse(response);
      setHintStatusMessage(null);
      hintVoteRef.current = null;
      setHintVote(initialVoteState);
    } catch (error) {
      setHintError(
        getErrorMessage(error, "Unable to generate a hint right now."),
      );
    } finally {
      setIsHintLoading(false);
    }
  }

  async function requestPracticeWithCount(nextPracticeCount: number) {
    if (!isPracticeEnabled || !aiHomeworkService) {
      setPracticeError(
        "Practice problems are not available for this question.",
      );
      return;
    }

    setPracticeError(null);
    setPracticeStatusMessage(null);
    setIsPracticeRegenerating(false);
    setIsPracticeLoading(true);

    try {
      const response = await aiHomeworkService.getPracticeQuestion({
        componentId,
        course,
        iteration: nextPracticeCount,
        questionUid: question.uid,
      });

      setPracticeResponse(response);
      setPracticeStatusMessage(null);
      setPracticeCount(nextPracticeCount + 1);
      setSelectedPracticeAnswer(null);
      setIsPracticeAnswerRevealed(false);
      practiceVoteRef.current = null;
      setPracticeVote(initialVoteState);
    } catch (error) {
      setPracticeError(
        getErrorMessage(
          error,
          "Unable to generate a practice problem right now.",
        ),
      );
    } finally {
      setIsPracticeLoading(false);
    }
  }

  async function voteOnHint(vote: AIHomeworkVote) {
    if (!aiHomeworkService || !hintResponse || hintVoteRef.current) {
      return;
    }

    const currentResponse = hintResponse;
    hintVoteRef.current = vote;
    setHintVote((current) => ({ ...current, error: null, isVoting: true }));

    if (vote === "down") {
      setHintResponse(null);
      setHintError(null);
      setHintStatusMessage(null);
      setIsHintRegenerating(true);
    }

    try {
      const result = await aiHomeworkService.submitVote({
        response: currentResponse,
        vote,
      });

      if (vote === "down" && !result.replacedContent) {
        setHintResponse(null);
        setHintStatusMessage("Updated, thanks for the feedback.");
      } else {
        setHintResponse(result.response);
        setHintStatusMessage(null);
      }

      setIsHintRegenerating(false);
      hintVoteRef.current = result.replacedContent ? null : vote;
      setHintVote({
        error: null,
        isVoting: false,
        userVote: result.replacedContent ? null : vote,
      });
    } catch (error) {
      hintVoteRef.current = hintVote.userVote;
      setHintResponse(currentResponse);
      setIsHintRegenerating(false);
      setHintStatusMessage(null);
      setHintVote({
        ...hintVote,
        error: getErrorMessage(
          error,
          "Failed to submit feedback. Please try again.",
        ),
        isVoting: false,
      });
    }
  }

  async function voteOnPractice(vote: AIHomeworkVote) {
    if (!aiHomeworkService || !practiceResponse || practiceVoteRef.current) {
      return;
    }

    const currentResponse = practiceResponse;
    practiceVoteRef.current = vote;
    setPracticeVote((current) => ({ ...current, error: null, isVoting: true }));

    if (vote === "down") {
      setPracticeResponse(null);
      setSelectedPracticeAnswer(null);
      setIsPracticeAnswerRevealed(false);
      setPracticeError(null);
      setPracticeStatusMessage(null);
      setIsPracticeRegenerating(true);
    }

    try {
      const result = await aiHomeworkService.submitVote({
        response: currentResponse,
        vote,
      });

      if (vote === "down" && !result.replacedContent) {
        setPracticeResponse(null);
        setPracticeStatusMessage("Updated, thanks for the feedback.");
      } else {
        const nextPracticeResponse = result.replacedContent
          ? toPracticeResponse(result.response, currentResponse)
          : {
              ...currentResponse,
              ...result.response,
              question: currentResponse.question,
            };

        setPracticeResponse(nextPracticeResponse);
        setPracticeStatusMessage(null);
      }

      setSelectedPracticeAnswer(null);
      setIsPracticeAnswerRevealed(false);
      setIsPracticeRegenerating(false);
      practiceVoteRef.current = result.replacedContent ? null : vote;
      setPracticeVote({
        error: null,
        isVoting: false,
        userVote: result.replacedContent ? null : vote,
      });
    } catch (error) {
      practiceVoteRef.current = practiceVote.userVote;
      setPracticeResponse(currentResponse);
      setIsPracticeRegenerating(false);
      setPracticeStatusMessage(null);
      setPracticeVote({
        ...practiceVote,
        error: getErrorMessage(
          error,
          "Failed to submit feedback. Please try again.",
        ),
        isVoting: false,
      });
    }
  }

  return {
    hint: {
      error: hintError,
      isLoading: isHintLoading,
      isRegenerating: isHintRegenerating,
      response: hintResponse,
      statusMessage: hintStatusMessage,
      vote: hintVote,
    },
    practice: {
      error: practiceError,
      isAnswerRevealed: isPracticeAnswerRevealed,
      isLoading: isPracticeLoading,
      isRegenerating: isPracticeRegenerating,
      practiceCount,
      question: practiceResponse?.question ?? null,
      response: practiceResponse,
      selectedAnswer: selectedPracticeAnswer,
      statusMessage: practiceStatusMessage,
      vote: practiceVote,
    },
    requestAnotherPractice: () => requestPracticeWithCount(practiceCount),
    requestHint,
    requestPractice: () => requestPracticeWithCount(0),
    revealPracticeAnswer: () => setIsPracticeAnswerRevealed(true),
    selectPracticeAnswer: (index: number) => {
      setSelectedPracticeAnswer(index);
      setIsPracticeAnswerRevealed(true);
    },
    voteOnHint,
    voteOnPractice,
  };
}
